import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { Button } from "@/components/ui/Button";
import {
	useDepositSavings,
	useSavingsBalance,
	useSavingsRateApr,
	useWithdrawSavings,
} from "@/hooks/useGovernance";
import { useTreasuryOverview } from "@/hooks/useTreasury";
import { useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import { ROUTES } from "@/lib/routes";
import { formatAmountPlain, formatNum } from "@/lib/utils";
import { useState } from "react";
import { Link } from "react-router-dom";

/** USDM DSR-lite — surplus-capped positive interest. */
export default function SavingsPage() {
	const { balances, isAuthenticated, isLoggingIn, login } = useAuth();
	const { data: savings = 0, isLoading } = useSavingsBalance();
	const { data: apr } = useSavingsRateApr();
	const { data: treasury } = useTreasuryOverview();
	const deposit = useDepositSavings();
	const withdraw = useWithdrawSavings();
	const [dep, setDep] = useState("");
	const [wd, setWd] = useState("");
	const depN = Number(dep) || 0;
	const wdN = Number(wd) || 0;
	const maxDep = depositableTokenMax(balances.USDM);

	return (
		<div className="container mx-auto max-w-xl space-y-6 px-4 py-6 sm:py-8 pb-16">
			<div className="flex items-start gap-3">
				<BrandTokenMark token="USDM" size="lg" />
				<div>
					<p className="section-eyebrow">USDM Savings</p>
					<h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
						Earn from surplus
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Deposit MotokoDollar (USDM) into the protocol savings pool.{" "}
						<strong className="text-foreground/90">DSR-lite</strong> pays a
						governance-set rate only when the surplus buffer can fund rewards —
						not a bank deposit guarantee.{" "}
						<Link to={ROUTES.treasury} className="font-semibold text-primary">
							Treasury →
						</Link>{" "}
						·{" "}
						<Link to={ROUTES.governance} className="font-semibold text-primary">
							Governance · MMK →
						</Link>
					</p>
				</div>
			</div>

			<div className="card-surface border border-border/60 bg-muted/15 p-4 text-xs leading-relaxed text-muted-foreground">
				<p className="font-semibold text-foreground">How rewards work</p>
				<ul className="mt-2 list-inside list-disc space-y-1">
					<li>
						Stability fees and other protocol revenue build{" "}
						<strong className="text-foreground/90">surplus</strong>; the waterfall
						earmarks a DSR share when status is Healthy.
					</li>
					<li>
						If surplus is tight or Critical, accrual slows or pauses — your
						principal stays in the pool until you withdraw.
					</li>
					<li>
						APR shown is the current on-chain savings rate, not a fixed APY
						promise.
					</li>
				</ul>
			</div>

			<div className="card-surface space-y-4 p-5">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<p className="text-[10px] uppercase text-muted-foreground">
							Your savings
						</p>
						<p className="mt-1 flex items-center gap-2 font-mono-amount text-3xl font-bold text-primary">
							<TokenIcon token="USDM" size="md" plain />
							{isLoading ? (
								"…"
							) : (
								<AnimatedNumber value={savings} decimals={4} />
							)}
						</p>
					</div>
					{apr != null ? (
						<span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
							DSR {apr.toFixed(2)}% APR
						</span>
					) : null}
				</div>
				{treasury ? (
					<p className="text-xs text-muted-foreground">
						Protocol surplus (cap for rewards):{" "}
						<span className="font-mono font-semibold text-foreground">
							{formatNum(treasury.surplusUsdm, 0)} USDM
						</span>
						{" · "}
						Pool deposited:{" "}
						<span className="font-mono font-semibold text-foreground">
							{formatNum(treasury.savingsDepositedUsdm, 0)} USDM
						</span>
					</p>
				) : null}
			</div>

			{!isAuthenticated ? (
				<div className="card-surface p-6 text-center">
					<p className="text-sm text-muted-foreground">
						Connect to deposit or withdraw savings.
					</p>
					<Button
						className="mt-4"
						variant="primary"
						loading={isLoggingIn}
						onClick={() => void login()}
					>
						Connect
					</Button>
				</div>
			) : (
				<div className="grid gap-4 sm:grid-cols-2">
					<form
						className="card-surface space-y-3 p-5"
						onSubmit={(e) => {
							e.preventDefault();
							if (depN > 0) deposit.mutate(depN);
						}}
					>
						<p className="text-sm font-semibold">Deposit USDM</p>
						<p className="text-[11px] text-muted-foreground">
							Wallet spendable{" "}
							<span className="font-mono font-semibold">
								{formatNum(maxDep, 4)}
							</span>{" "}
							(after fees)
						</p>
						<div className="relative">
							<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
								<TokenIcon token="USDM" size="sm" plain />
							</span>
							<input
								value={dep}
								onChange={(e) => setDep(e.target.value)}
								placeholder="0.00"
								inputMode="decimal"
								className="h-12 w-full rounded-xl border border-border bg-muted/40 py-2 pl-11 pr-14 font-mono-amount outline-none focus:border-primary"
							/>
							<span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
								USDM
							</span>
						</div>
						<button
							type="button"
							className="text-xs font-semibold text-primary"
							onClick={() => setDep(formatAmountPlain(maxDep, 4))}
						>
							Max
						</button>
						<RequireAuthAction
							labelWhenUnauthed="Connect"
							disabled={deposit.isPending || depN <= 0 || depN > maxDep + 1e-12}
							onAuthenticated={() => deposit.mutate(depN)}
						>
							{deposit.isPending ? "Depositing…" : "Deposit USDM"}
						</RequireAuthAction>
					</form>

					<form
						className="card-surface space-y-3 p-5"
						onSubmit={(e) => {
							e.preventDefault();
							if (wdN > 0) withdraw.mutate(wdN);
						}}
					>
						<p className="text-sm font-semibold">Withdraw USDM</p>
						<p className="text-[11px] text-muted-foreground">
							In savings{" "}
							<span className="font-mono font-semibold">
								{formatNum(savings, 4)}
							</span>
						</p>
						<div className="relative">
							<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
								<TokenIcon token="USDM" size="sm" plain />
							</span>
							<input
								value={wd}
								onChange={(e) => setWd(e.target.value)}
								placeholder="0.00"
								inputMode="decimal"
								className="h-12 w-full rounded-xl border border-border bg-muted/40 py-2 pl-11 pr-14 font-mono-amount outline-none focus:border-primary"
							/>
							<span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
								USDM
							</span>
						</div>
						<button
							type="button"
							className="text-xs font-semibold text-primary"
							onClick={() => setWd(formatAmountPlain(savings, 4))}
						>
							Max
						</button>
						<Button
							type="submit"
							variant="secondary"
							fullWidth
							disabled={withdraw.isPending || wdN <= 0}
						>
							{withdraw.isPending ? "Withdrawing…" : "Withdraw"}
						</Button>
					</form>
				</div>
			)}
		</div>
	);
}
